import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RiEditLine, RiSaveLine } from 'react-icons/ri';
import * as Yup from 'yup';
import { MaskedInput } from '../../../../../../../../../../../components/Form/MaskedInput';
import { ReactSelect } from '../../../../../../../../../../../components/Form/ReactSelect';
import { useActivity } from '../../../../../../../../../../../hooks/activity';
import { updateActivityInvoiceConfigsService } from '../../../../../../../../../../../services/Activities/UpdateActivityInvoiceConfigsService';
import { translateError } from '../../../../../../../../../../../utils/errors';

interface IUpdateActivityInvoiceConfigFormData {
  bookingCostCenter?: string;
  bookingProductCode?: string;
  bookingProductType?: string;
  bookingProductNature?: string;
  bookingInvoiceType: 'NF' | 'ND';
  guestBookingCostCenter?: string;
  guestBookingProductCode?: string;
  guestBookingProductType?: string;
  guestBookingProductNature?: string;
  guestBookingInvoiceType: 'NF' | 'ND';
}

const updateActivityInvoiceConfigSchema = Yup.object().shape({
  bookingCostCenter: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  bookingProductCode: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  bookingProductType: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  bookingProductNature: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  bookingInvoiceType: Yup.string()
    .oneOf(['NF', 'ND', null], 'Tipo inválido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Requerido'),
  guestBookingCostCenter: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  guestBookingProductCode: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  guestBookingProductType: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  guestBookingProductNature: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  guestBookingInvoiceType: Yup.string()
    .oneOf(['NF', 'ND', null], 'Tipo inválido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Requerido'),
});

export const ActivityInvoiceConfig = (props: BoxProps): JSX.Element => {
  const toast = useToast();

  const { activity, handleActivity } = useActivity();

  const [isEditing, setIsEditing] = useState(false);

  const { control, register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(updateActivityInvoiceConfigSchema),
  });

  const { errors } = formState;

  const invoiceTypeSelectOptions = useMemo(
    () => [
      {
        label: 'Nota fiscal',
        value: 'NF',
      },
      {
        label: 'Nota débito',
        value: 'ND',
      },
    ],
    [],
  );

  const toggleIsEditing = useCallback(() => {
    setIsEditing((prevState) => !prevState);
  }, []);

  useEffect(() => {
    reset({
      bookingCostCenter: activity?.invoiceConfig.bookingCostCenter,
      bookingProductCode: activity?.invoiceConfig.bookingProductCode,
      bookingProductType: activity?.invoiceConfig.bookingProductType,
      bookingProductNature: activity?.invoiceConfig.bookingProductNature,
      bookingInvoiceType: activity?.invoiceConfig.bookingInvoiceType,
      guestBookingCostCenter: activity?.invoiceConfig.guestBookingCostCenter,
      guestBookingProductCode: activity?.invoiceConfig.guestBookingProductCode,
      guestBookingProductType: activity?.invoiceConfig.guestBookingProductType,
      guestBookingProductNature:
        activity?.invoiceConfig.guestBookingProductNature,
      guestBookingInvoiceType: activity?.invoiceConfig.guestBookingInvoiceType,
    });
  }, [
    reset,
    activity?.invoiceConfig.bookingCostCenter,
    activity?.invoiceConfig.bookingProductCode,
    activity?.invoiceConfig.bookingProductType,
    activity?.invoiceConfig.bookingProductNature,
    activity?.invoiceConfig.guestBookingCostCenter,
    activity?.invoiceConfig.guestBookingProductCode,
    activity?.invoiceConfig.guestBookingProductType,
    activity?.invoiceConfig.guestBookingProductNature,
    activity?.invoiceConfig.guestBookingInvoiceType,
    activity?.invoiceConfig.bookingInvoiceType,
  ]);

  const updateActivityInvoiceConfig = async ({
    bookingCostCenter,
    bookingProductCode,
    bookingProductType,
    bookingProductNature,
    bookingInvoiceType,
    guestBookingCostCenter,
    guestBookingProductCode,
    guestBookingProductType,
    guestBookingProductNature,
    guestBookingInvoiceType,
  }: IUpdateActivityInvoiceConfigFormData): Promise<void> => {
    if (!activity) {
      return;
    }

    try {
      const invoiceConfig = await updateActivityInvoiceConfigsService({
        activityId: activity.id,
        bookingCostCenter,
        bookingProductCode,
        bookingProductType,
        bookingProductNature,
        bookingInvoiceType,
        guestBookingCostCenter,
        guestBookingProductCode,
        guestBookingProductType,
        guestBookingProductNature,
        guestBookingInvoiceType,
      });

      handleActivity({ ...activity, invoiceConfig });

      toggleIsEditing();

      toast({
        title: 'Atualizado com sucesso',
        description: 'Configurações fiscais atualizadas corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao atualizar',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao atualizar configurações fiscais, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  return (
    <Box
      as="form"
      borderRadius={8}
      flex="1"
      bg="white"
      p="8"
      onSubmit={handleSubmit(updateActivityInvoiceConfig)}
      {...props}
    >
      <HStack justifyContent="space-between">
        <Text textColor="red.500">
          * Requerido para emissão de notas fiscais das reservas pagas
        </Text>

        <ButtonGroup>
          <Button
            isDisabled={isEditing}
            size="sm"
            fontSize="sm"
            color="white"
            colorScheme="yellow"
            onClick={toggleIsEditing}
            leftIcon={<Icon as={RiEditLine} fontSize="20" />}
          >
            Editar
          </Button>

          <Button
            isDisabled={!isEditing}
            size="sm"
            type="submit"
            colorScheme="green"
            leftIcon={<Icon as={RiSaveLine} fontSize="20" />}
            isLoading={formState.isSubmitting}
          >
            Salvar
          </Button>
        </ButtonGroup>
      </HStack>

      <Divider my="6" borderColor="gray.300" />

      <VStack alignItems="flex-start" spacing="8">
        <VStack alignItems="flex-start" w="100%">
          <Heading size="md">Reservas</Heading>

          <SimpleGrid minChildWidth="240px" spacing="6" w="100%">
            <MaskedInput
              label="Centro de custo"
              isDisabled={!isEditing}
              error={errors.bookingCostCenter}
              {...register('bookingCostCenter')}
            />

            <MaskedInput
              label="Código de produto"
              isDisabled={!isEditing}
              error={errors.bookingProductCode}
              {...register('bookingProductCode')}
            />

            <MaskedInput
              label="Tipo de produto"
              isDisabled={!isEditing}
              error={errors.bookingProductType}
              {...register('bookingProductType')}
            />

            <MaskedInput
              label="Natureza de produto"
              isDisabled={!isEditing}
              error={errors.bookingProductNature}
              {...register('bookingProductNature')}
            />

            <ReactSelect
              isDisabled={!isEditing}
              label="Tipo de nota"
              name="bookingInvoiceType"
              options={invoiceTypeSelectOptions}
              control={control}
              error={errors.bookingInvoiceType}
            />
          </SimpleGrid>
        </VStack>

        <VStack alignItems="flex-start" w="100%">
          <Heading size="md">Reservas de convidados</Heading>

          <SimpleGrid minChildWidth="240px" spacing="6" w="100%">
            <MaskedInput
              label="Centro de custo"
              isDisabled={!isEditing}
              error={errors.guestBookingCostCenter}
              {...register('guestBookingCostCenter')}
            />

            <MaskedInput
              label="Código de produto"
              isDisabled={!isEditing}
              error={errors.guestBookingProductCode}
              {...register('guestBookingProductCode')}
            />

            <MaskedInput
              label="Tipo de produto"
              isDisabled={!isEditing}
              error={errors.guestBookingProductType}
              {...register('guestBookingProductType')}
            />

            <MaskedInput
              label="Natureza de produto"
              isDisabled={!isEditing}
              error={errors.guestBookingProductNature}
              {...register('guestBookingProductNature')}
            />

            <ReactSelect
              isDisabled={!isEditing}
              label="Tipo de nota"
              name="guestBookingInvoiceType"
              options={invoiceTypeSelectOptions}
              control={control}
              error={errors.guestBookingInvoiceType}
            />
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  );
};
